import Alpine from 'alpinejs';

window.addEventListener('load', initializeUserDataFetchers);

/**
 * Initializes the user data fetchers on page load.
 * Fetches the current session and processes all relevant form fields.
 */
async function initializeUserDataFetchers() {
  const currentSession = (await window.andel?.auth?.currentSession?.()) ?? {};

  const dataFetcherFields = document.querySelectorAll(
    '.wpforms-field-user_data_fetcher'
  );

  for (const dataFetcherField of dataFetcherFields) {
    const fieldData = JSON.parse(dataFetcherField.dataset.fieldData ?? 'null');
    if (!fieldData) {
      continue;
    }

    // If the user isn't logged in to the self service, we cannot fetch data.
    if (!currentSession.authenticated) {
      const { required } = fieldData;

      // If the field is required, display an error message and prevent the form
      // from submitting. Sadly, it looks like WPForms saves toggles as the
      // string "1" or undefined, so we cast it to a boolean.
      if (Boolean(Number(required))) {
        setupErrorForRequiredField(dataFetcherField, fieldData);
        return;
      }
      continue;
    }

    await populateFieldWithUserData(dataFetcherField, fieldData);
  }
}

/**
 * Sets up the error message and form validation for a required unauthenticated field.
 * @param dataFetcherField - The form field element.
 * @param fieldData - The parsed field data.
 */
function setupErrorForRequiredField(dataFetcherField, fieldData) {
  const { errorMessage, showErrorAbove } = fieldData;
  const formEl = dataFetcherField.closest('form');

  // Find the form field that we need to show the error above.
  const errorMsgEl =
    formEl?.querySelector(`[data-field-id="${showErrorAbove}"]`) ||
    dataFetcherField;

  // Create the error message element, and display it above the form field.
  const errorEl = document.createElement('p');
  errorEl.classList.add('hidden-field-required-error');
  errorEl.innerText = errorMessage;
  errorMsgEl.before(errorEl);

  // Create a form validator method that never passes.
  const methodName = `${formEl.id}-hidden-required`;
  jQuery.validator.addMethod(methodName, () => false, '');

  // Add the validator method to the form field where the error message is
  // shown, so the field is focused when the user tries to submit the form.
  const errorMsgInput = errorMsgEl.querySelector(
    'input[name^="wpforms[fields]"]'
  );
  jQuery(errorMsgInput).rules('add', methodName);
}

/**
 * Populates the form field input with fetched user data.
 * @param dataFetcherField - The form field element.
 * @param fieldData - The parsed field data.
 */
async function populateFieldWithUserData(dataFetcherField, fieldData) {
  dataFetcherField.querySelector('input').value = await getUserData(fieldData);
}

/**
 * Retrieves the appropriate user data based on the fetch type.
 * @param fieldData - The field data containing the fetch type.
 */
const getUserData = (fieldData) => {
  if (fieldData.fetch === 'name') {
    return getName();
  }

  if (fieldData.fetch === 'email') {
    return getEmail();
  }

  if (fieldData.fetch === 'user_id') {
    return getUserId();
  }

  if (fieldData.fetch === 'customer_ids') {
    return getCustomerIds();
  }

  if (fieldData.fetch === 'address') {
    return getAddress();
  }
};

/**
 * Fetches and formats the user's full name.
 */
const getName = async () => {
  const profile = await fetchProfile();
  if (!profile) return null;

  return `${profile.first_name} ${profile.last_name}`;
};

/**
 * Fetches the user's email.
 */
const getEmail = async () => {
  const profile = await fetchProfile();
  if (!profile) return null;

  return profile.email;
};

/**
 * Fetches the user's ID.
 */
const getUserId = async () => {
  const profile = await fetchProfile();
  if (!profile) return null;

  return profile.person_id;
};

/**
 * Fetches and formats the user's customer IDs as a comma-separated string.
 */
const getCustomerIds = async () => {
  const profile = await fetchProfile();
  if (!profile) return null;

  return profile.customer_relationships.map(({ id }) => id).join(', ');
};

/**
 * Fetches the user's full address.
 */
const getAddress = async () => {
  const profile = await fetchProfile();
  if (!profile) return null;

  return profile.full_address;
};

const fetchedData = {};

/**
 * Fetches the user's profile data from the server, with caching.
 */
const fetchProfile = () => {
  if (fetchedData.profile) {
    return fetchedData.profile;
  }

  const loginStore = Alpine.store('Login');

  return fetch(loginStore.serviceLayerEndpoints.profile(), {
    credentials: 'include',
    headers: {
      Authorization: `bearer ${loginStore.getTokenFromSessionStorage(
        loginStore.storedTokens.ACCESS_TOKEN
      )}`,
      IdToken: loginStore.getTokenFromSessionStorage(
        loginStore.storedTokens.ID_TOKEN
      ),
    },
  })
    .then((response) => {
      const data = response.json();
      fetchedData.profile = data;
      return data;
    })
    .catch((error) => {
      console.error(error);
      return null;
    });
};
